import React, { FC, HTMLProps } from 'react';

import { LucidTheme } from '@lucid/theme';
import styled from '@emotion/styled';

import Box from '../box/box';
import Icon, { IconTypes } from '../icon/icon';

export interface InputProps extends HTMLProps<HTMLInputElement> {
  /** Allows passing additional styles to the component. */
  className?: string;
  /** Checks if the icon should be displayed in the Input component. */
  hasIcon?: boolean;
  /** An icon to be added to the left side of the Input component. */
  icon?: IconTypes;
  /** A string that prepends on the Input name. */
  key?: string;
  /** The placeholder text for the Input component. */
  placeholder?: string;
  /** The available variants for the Input component. */
  variant?: 'default';
}

const StyledInput = styled.input<InputProps>(({ hasIcon, theme }) => [
  {
    borderColor: theme.colors.coolGrey[100],
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: theme.radii.base,
    paddingTop: theme.space.s16,
    paddingRight: theme.space.s16,
    paddingBottom: theme.space.s16,
    paddingLeft: theme.space.s16,
  },
  hasIcon && {
    paddingLeft: theme.space.s40,
  },
]);

const Input: FC<Omit<InputProps, 'as'>> = ({
  className,
  hasIcon = false,
  icon = 'MagnifyingGlass',
  key = 'input',
  placeholder = 'Type something...',
  variant = 'default',
  ...rest
}) => {
  return (
    <Box
      css={{ position: 'relative' }}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
    >
      {hasIcon && (
        <Icon
          css={(theme: LucidTheme) => ({
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            paddingLeft: theme.space.s16,
          })}
          type={icon}
        />
      )}
      <StyledInput {...rest} key={`${key}-input`} hasIcon={hasIcon} placeholder={placeholder} variant={variant} />
    </Box>
  );
};

export { Input };
export default Input;
